import type { PreviewData, TaxDistributionItem } from '@/features/campaigns/types/campaign';

export type BillTaxRow = {
  key: string;
  label: string;
  amount: number;
};

export type ResolvedBill = {
  subtotal: number;
  discount: number;
  totalTax: number;
  amountWithTax: number;
  payable: number;
  taxRows: BillTaxRow[];
};

function num(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function taxItemAmount(item: TaxDistributionItem): number {
  return num(item.totalAmount ?? item.amount);
}

/**
 * Resolve a mathematically reconcilable bill from the step-3 / reprice payload.
 * Prefers authoritative `pricing.*` fields; does not invent tax rates.
 */
export function resolvePreviewBill(data: PreviewData | undefined | null): ResolvedBill {
  const pricing = data?.pricing;
  const summary = data?.summary;
  const coupon = data?.coupon;

  const subtotal = num(
    pricing?.amountWithoutTax ?? summary?.amountWithoutTax,
  );
  const discount = num(
    pricing?.discountAmount ??
      pricing?.totalDiscountAmount ??
      summary?.discountAmount ??
      coupon?.discountAmount,
  );
  const amountWithTax = num(pricing?.amountWithTax ?? summary?.amountWithTax);
  const payable = num(
    pricing?.totalAmount ?? Math.max(0, amountWithTax - discount),
  );
  const totalTax = num(pricing?.totalTax ?? summary?.totalTax);

  const rawDist = (summary?.taxDistribution ?? []).filter(Boolean);
  const taxRowsFromDist: BillTaxRow[] = rawDist
    .map((item, index) => {
      const amount = taxItemAmount(item);
      const role = String(item.role || 'Tax');
      const label = item.percentage
        ? `${role} (${item.percentage}%)`
        : role;
      return {
        key: `${role}-${index}`,
        label,
        amount,
      };
    })
    .filter((row) => row.amount > 0);

  let taxRows = taxRowsFromDist;

  // If distribution rows are missing/zero but API reported a tax total, show one GST line.
  const distSum = taxRows.reduce((sum, row) => sum + row.amount, 0);
  if (taxRows.length === 0 && totalTax > 0) {
    const breakdown = pricing?.taxBreakdown ?? summary?.taxBreakdown ?? {};
    const breakdownRows = Object.entries(breakdown)
      .map(([key, value]) => ({
        key,
        label: key.toUpperCase(),
        amount: num(value),
      }))
      .filter((row) => row.amount > 0);

    taxRows =
      breakdownRows.length > 0
        ? breakdownRows
        : [{ key: 'gst', label: 'GST', amount: totalTax }];
  } else if (taxRows.length > 0 && distSum === 0 && totalTax > 0) {
    taxRows = [{ key: 'gst', label: 'GST', amount: totalTax }];
  }

  return {
    subtotal,
    discount,
    totalTax: taxRows.reduce((sum, row) => sum + row.amount, 0) || totalTax,
    amountWithTax,
    payable,
    taxRows,
  };
}

/**
 * Resolve booked-slot count.
 * Backend Step 3 sets `analytics.totalSlots = bookedSlots.length`
 * (unique booked hour entries across the campaign). Prefer API analytics,
 * then `bookedSlots` array length, then Step 3 local booking count.
 * Does not fabricate values when all sources are absent.
 */
export function resolveAnalyticsSlotCount(
  analytics: Record<string, unknown> | undefined,
  fallbackSlotCount?: number,
  bookedSlotsArray?: unknown[] | null,
): number | null {
  const candidates: unknown[] = [
    analytics?.totalSlots,
    analytics?.totalSlot,
    analytics?.slotCount,
    Array.isArray(analytics?.bookedSlots)
      ? (analytics?.bookedSlots as unknown[]).length
      : analytics?.bookedSlots,
    Array.isArray(bookedSlotsArray) ? bookedSlotsArray.length : null,
    fallbackSlotCount,
  ];
  for (const value of candidates) {
    if (value == null || value === '') continue;
    const n = Number(value);
    if (Number.isFinite(n) && n >= 0) {
      // Prefer positive counts; allow 0 only from explicit booking arrays
      if (n > 0) return n;
    }
  }
  if (Array.isArray(bookedSlotsArray) && bookedSlotsArray.length === 0) return 0;
  if (fallbackSlotCount === 0) return 0;
  return null;
}
